import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export interface CartItem {
  productId: string;
  productName: string;
  quantity: number;
  unitPrice: number;
  totalPrice: number;
  selectedVariants?: Record<string, string>;
  isPeptide?: boolean; // Drives the "filled by" pharmacy hint (Blue Five vs Greenstone)
}

export interface CartPatient {
  id: string;
  firstName: string;
  lastName: string;
  fullName: string;
}

interface CartStore {
  // State
  items: CartItem[];
  selectedPatient: CartPatient | null;
  isOpen: boolean;
  shippingAmount: number;  // Shipping fee (fetched from backend)
  shippingLoading: boolean;

  // Computed values
  totalItems: number;
  subtotal: number;       // Sum of items (before shipping)
  totalAmount: number;    // subtotal + shipping

  // Actions
  addItem: (product: {
    id: string;
    name: string;
    price: number;
    selectedVariants?: Record<string, string>;
    isPeptide?: boolean;
  }, quantity?: number) => void;

  removeItem: (productId: string) => void;
  updateQuantity: (productId: string, quantity: number) => void;
  clearCart: () => void;

  setSelectedPatient: (patient: CartPatient | null) => void;
  setShippingAmount: (amount: number) => void;
  setShippingLoading: (loading: boolean) => void;

  toggleCart: () => void;
  openCart: () => void;
  closeCart: () => void;

  // Order creation
  canCreateOrder: () => boolean;
  getOrderData: () => {
    patientId: string;
    items: {
      productId: string;
      quantity: number;
      selectedVariants?: Record<string, string>;
    }[];
  } | null;
}

export const useCartStore = create<CartStore>()(
  persist(
    (set, get) => ({
      // Initial state
      items: [],
      selectedPatient: null,
      isOpen: false,
      shippingAmount: 0,
      shippingLoading: false,

      // Computed values
      get totalItems() {
        return get().items.reduce((total, item) => total + item.quantity, 0);
      },

      get subtotal() {
        return get().items.reduce((total, item) => total + item.totalPrice, 0);
      },

      get totalAmount() {
        const state = get();
        return state.subtotal + state.shippingAmount;
      },
      
      // Actions
      addItem: (product, quantity = 1) => {
        set((state) => {
          const existingItem = state.items.find(item => 
            item.productId === product.id && 
            JSON.stringify(item.selectedVariants) === JSON.stringify(product.selectedVariants)
          );
          
          if (existingItem) {
            // Update existing item
            const updatedItems = state.items.map(item =>
              item === existingItem
                ? {
                    ...item,
                    quantity: item.quantity + quantity,
                    totalPrice: (item.quantity + quantity) * item.unitPrice
                  }
                : item
            );
            return { items: updatedItems };
          } else {
            // Add new item
            const newItem: CartItem = {
              productId: product.id,
              productName: product.name,
              quantity,
              unitPrice: product.price,
              totalPrice: product.price * quantity,
              selectedVariants: product.selectedVariants,
              isPeptide: product.isPeptide
            };
            return { items: [...state.items, newItem] };
          }
        });
      },
      
      removeItem: (productId) => {
        set((state) => ({
          items: state.items.filter(item => item.productId !== productId)
        }));
      },
      
      updateQuantity: (productId, quantity) => {
        if (quantity <= 0) {
          get().removeItem(productId);
          return;
        }
        
        set((state) => ({
          items: state.items.map(item =>
            item.productId === productId
              ? {
                  ...item,
                  quantity,
                  totalPrice: quantity * item.unitPrice
                }
              : item
          )
        }));
      },
      
      clearCart: () => {
        set({ items: [], selectedPatient: null, shippingAmount: 0 });
      },
      
      setSelectedPatient: (patient) => {
        set({ selectedPatient: patient });
      },

      setShippingAmount: (amount) => {
        set({ shippingAmount: amount });
      },

      setShippingLoading: (loading) => {
        set({ shippingLoading: loading });
      },

      toggleCart: () => {
        set((state) => ({ isOpen: !state.isOpen }));
      },
      
      openCart: () => {
        set({ isOpen: true });
      },
      
      closeCart: () => {
        set({ isOpen: false });
      },
      
      canCreateOrder: () => {
        const state = get();
        return state.items.length > 0 && state.selectedPatient !== null;
      },
      
      getOrderData: () => {
        const state = get();
        if (!state.canCreateOrder()) {
          return null;
        }
        
        return {
          patientId: state.selectedPatient!.id,
          items: state.items.map(item => ({
            productId: item.productId,
            quantity: item.quantity,
            selectedVariants: item.selectedVariants
          }))
        };
      }
    }),
    {
      name: 'bloom-cart',
      // Only persist essential data, not UI state
      partialize: (state) => ({
        items: state.items,
        selectedPatient: state.selectedPatient
      })
    }
  )
); 